import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@mui/material';
import {
  SchemeForm,
  SchemeFormSchema,
  SchemeFormWorkout,
  TrainingType,
} from '@strength/domain';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Drawer,
  DrawerFooter,
  DrawerHeader,
} from '../../../../components/drawer';
import { Form } from '../../../../components/form/form';
import {
  DeleteConfirmationModal,
  deleteSchemeText,
} from '../../../../components/modals/delete-confirmation-modal';
import { useViewer } from '../../../../context/viewer';
import { DisabledWithTooltip } from '../../../../components/permissions/DisabledWithTooltip';
import { FocusOption, Scheme } from '../types';
import { SchemeSheetContent } from './SchemeSheetContent';

export const EditSchemeSheet = ({
  scheme,
  open,
  focusOptions,
  trainingTypeOptions,
  onOpenChange,
  onSubmit,
  onDelete,
}: {
  scheme: Scheme;
  open: boolean;
  focusOptions: FocusOption[];
  trainingTypeOptions: TrainingType[];
  onOpenChange: () => void;
  onSubmit: (id: string, scheme: SchemeForm) => void;
  onDelete: (id: string) => void;
}) => {
  const { t } = useTranslation();
  const { hasPermissions } = useViewer();
  const disableEdit = !hasPermissions('library.schemes', 'edit');
  const disableDelete = !hasPermissions('library.schemes', 'delete');
  const { id } = scheme;
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const getValuesForSchemeForm = (scheme: Scheme): SchemeForm => {
    const { metrics, sets, trainingType, ...rest } = scheme;

    const numCycles =
      sets.length > 0 ? Math.max(...sets.map((s) => s.cycleIndex), 0) + 1 : 0;
    const cycles = Array.from(Array(numCycles).keys());

    const orderedWorkouts: SchemeFormWorkout[] = cycles.map((cycleIndex) => {
      const setsForCycle = sets.filter((s) => s.cycleIndex === cycleIndex);
      const numSets = Array.from(Array(setsForCycle.length).keys());
      return {
        sets: numSets
          .map((setIndex) => {
            const set = setsForCycle.find((s) => s.sortOrder === setIndex);
            return set ?? null;
          })
          .filter((s) => s !== null),
      } as SchemeFormWorkout;
    });

    return {
      ...rest,
      trainingTypeId: trainingType.id,
      metrics,
      workouts: orderedWorkouts,
      focusIds: rest.focuses.map((f) => f.focusId),
    };
  };

  const form = useForm<SchemeForm>({
    resolver: zodResolver(SchemeFormSchema),
    defaultValues: getValuesForSchemeForm(scheme),
    mode: 'onChange',
  });
  const { handleSubmit } = form;

  const onFormSubmit = (scheme: SchemeForm) => {
    onSubmit(id, scheme);
    onOpenChange();
  };

  const onHandleDelete = () => {
    onDelete(id);
    onOpenChange();
  };

  return (
    <Drawer open={open} onClose={() => onOpenChange()}>
      <DrawerHeader title={t('editScheme')} onClose={() => onOpenChange()} />

      <Form {...form}>
        <form
          id="edit-scheme-form"
          className="gap-4 flex flex-col overflow-y-hidden flex-grow p-1"
          onSubmit={handleSubmit(onFormSubmit)}
        >
          <div className="px-8 py-4 grow">
            <SchemeSheetContent
              form={form}
              focusOptions={focusOptions}
              trainingTypeOptions={trainingTypeOptions}
              isEdit={true}
            />
          </div>
        </form>
      </Form>
      <DrawerFooter className="flex flex-row w-full p-8 justify-between">
        <DisabledWithTooltip
          disabled={disableDelete}
          reason={t('permissionDisabled.delete', { item: t('schemes') })}
        >
          <Button
            variant="containedDestructive"
            onClick={() => setDeleteModalOpen(true)}
          >
            {t('delete')}
          </Button>
        </DisabledWithTooltip>
        <div className="flex flex-row gap-2">
          <Button variant="outlined" onClick={() => onOpenChange()}>
            {t('cancel')}
          </Button>
          <Button type="submit" form="edit-scheme-form" disabled={disableEdit}>
            {t('save')}
          </Button>
        </div>
      </DrawerFooter>
      <DeleteConfirmationModal
        id={id ?? ''}
        open={deleteModalOpen}
        {...deleteSchemeText(t)}
        onClose={() => setDeleteModalOpen(false)}
        onDelete={() => onHandleDelete()}
      />
    </Drawer>
  );
};
